/**
 * Azure Resources Page (Admin only) — NC-540 / M16 Phase 2
 *
 * Live inventory of Azure resources across all configured workload
 * subscriptions, sourced from `GET /api/azure-resources` (the ARG-backed
 * endpoint added in NC-539). Three tabs:
 *
 *  - Inventory: full filtered listing.
 *  - Drift:    ARG resources of type `virtualmachines` that don't appear
 *              in any Cosmos `Run.accounts[].vm.vmName` — i.e. azure-side
 *              VMs the campus portal does not know about.
 *  - Orphans:  ARG resources tagged `Role=PackerBuild` or `ManagedBy=Packer`
 *              (Packer image-build leftovers).
 *
 * Out of scope for this PR (tracked as M16 follow-ups):
 *  - Orphan age filter (`> 4h`) — ARG listing does not surface `timeCreated`.
 *  - Orphan "no active GHA run" cross-check — needs either a BE endpoint
 *    or a client-side gh-API call.
 *  - DataGrid + React-Query: issue body mentions them, but neither
 *    `@mui/x-data-grid` nor `@tanstack/react-query` is in the project.
 *    Using the existing MUI `Table` + `useApiQuery` pattern (matches
 *    AuditPage / InstallersPage).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { azureResourcesApi, runsApi } from '../services/api';
import {
  AzureResourceListing,
  AzureResourceListingResponse,
  PaginatedResponse,
  ResourceMetadata,
  Run,
} from '../services/types';
import { TableSkeleton } from '../components/SkeletonLoaders';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';
import { useAuth } from '../contexts/AuthContext';
import { useApiQuery } from '../hooks/useApiQuery';

type TabKey = 'inventory' | 'drift' | 'orphans';

const TYPE_OPTIONS = [
  { value: '', label: 'Alle Typen (Standard: VM/Disk/NIC/PIP)' },
  { value: 'microsoft.compute/virtualmachines', label: 'Virtual Machines' },
  { value: 'microsoft.compute/disks', label: 'Disks' },
  { value: 'microsoft.network/networkinterfaces', label: 'Network Interfaces' },
  { value: 'microsoft.network/publicipaddresses', label: 'Public IPs' },
  { value: '*', label: 'Alle Resource-Types' },
];

const PACKER_TAG_MATCHERS: Array<(tags: Record<string, string>) => boolean> = [
  (t) => t.Role === 'PackerBuild',
  (t) => t.ManagedBy === 'Packer',
];

function isPackerOrphan(r: AzureResourceListing): boolean {
  if (!r.type.startsWith('microsoft.compute/virtualmachines')) return false;
  return PACKER_TAG_MATCHERS.some((m) => m(r.tags));
}

function shortType(t: string): string {
  // 'microsoft.compute/virtualmachines' -> 'compute/virtualmachines'
  return t.replace(/^microsoft\./, '');
}

function shortPowerState(p?: string): string | null {
  if (!p) return null;
  return p.replace(/^PowerState\//, '');
}

const KNOWN_TAG_KEYS = ['Project', 'RunId', 'Workload', 'Owner', 'Role', 'ManagedBy'];

export default function AzureResourcesPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  const [tab, setTab] = useState<TabKey>('inventory');
  const [nameFilter, setNameFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [tagFilter, setTagFilter] = useState(''); // 'key=value'

  // NC-543: side-drawer state. `selected` controls Drawer open/close; the
  // form state is kept locally and seeded from the fetched metadata.
  const [selected, setSelected] = useState<AzureResourceListing | null>(null);
  const [formNotes, setFormNotes] = useState('');
  const [formOwner, setFormOwner] = useState('');
  const [formBusinessContext, setFormBusinessContext] = useState('');
  const [formLinkedRunId, setFormLinkedRunId] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ARG query params follow the BE contract; empty strings drop to undefined.
  const queryParams = useMemo(
    () => ({
      type: typeFilter || undefined,
      subscription: subscriptionFilter || undefined,
      tag: tagFilter || undefined,
      name: nameFilter || undefined,
    }),
    [typeFilter, subscriptionFilter, tagFilter, nameFilter],
  );

  const {
    data: resourcesPayload,
    loading: resourcesLoading,
    error: resourcesError,
    refetch: refetchResources,
  } = useApiQuery<AzureResourceListingResponse>(
    () => azureResourcesApi.list(queryParams),
    [queryParams.type, queryParams.subscription, queryParams.tag, queryParams.name, isAdmin],
    { enabled: isAdmin },
  );

  // Runs are only fetched on the Drift tab — they're not needed for inventory or
  // orphan computation, and an extra paginated runs call on every admin visit
  // would be wasteful. The Drift cross-check joins runs.accounts[].vm.vmName
  // against ARG resources of type virtualmachines.
  const {
    data: runsPayload,
    loading: runsLoading,
    error: runsError,
  } = useApiQuery<PaginatedResponse<Run>>(
    () => runsApi.list({ pageSize: 100 }),
    [isAdmin, tab],
    { enabled: isAdmin && tab === 'drift' },
  );

  // Memoise `allResources` so it keeps a stable reference across renders —
  // otherwise the `tabResources` useMemo below would invalidate on every
  // render (eslint react-hooks/exhaustive-deps).
  const allResources: AzureResourceListing[] = useMemo(
    () => resourcesPayload?.resources ?? [],
    [resourcesPayload],
  );

  const knownVmNames = useMemo(() => {
    const set = new Set<string>();
    for (const run of runsPayload?.items ?? []) {
      for (const acc of run.accounts ?? []) {
        if (acc.vm?.vmName) set.add(acc.vm.vmName);
      }
    }
    return set;
  }, [runsPayload]);

  const tabResources: AzureResourceListing[] = useMemo(() => {
    switch (tab) {
      case 'drift':
        return allResources
          .filter((r) => r.type === 'microsoft.compute/virtualmachines')
          .filter((r) => !knownVmNames.has(r.name));
      case 'orphans':
        return allResources.filter(isPackerOrphan);
      default:
        return allResources;
    }
  }, [tab, allResources, knownVmNames]);

  const loading = resourcesLoading || (tab === 'drift' && runsLoading);
  const error = resourcesError || (tab === 'drift' ? runsError : null);

  // Metadata for the currently selected resource. Disabled when the drawer
  // is closed so the API isn't pinged for every row hover.
  const selectedResourceId = selected?.id;
  const {
    data: metadata,
    loading: metadataLoading,
    refetch: refetchMetadata,
  } = useApiQuery<ResourceMetadata | null>(
    () => azureResourcesApi.getMetadata(selectedResourceId as string),
    [selectedResourceId],
    { enabled: !!selectedResourceId },
  );

  // Seed the form whenever the drawer opens or the fetched metadata changes.
  useEffect(() => {
    if (!selected) return;
    setFormNotes(metadata?.notes ?? '');
    setFormOwner(metadata?.owner ?? '');
    setFormBusinessContext(metadata?.businessContext ?? '');
    setFormLinkedRunId(metadata?.linkedRunId ?? '');
    setSaveError(null);
  }, [selected, metadata]);

  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    setSaveError(null);
    try {
      await azureResourcesApi.upsertMetadata({
        resourceId: selected.id,
        notes: formNotes || undefined,
        owner: formOwner || undefined,
        businessContext: formBusinessContext || undefined,
        linkedRunId: formLinkedRunId || undefined,
      });
      await refetchMetadata();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  // Admin-guard runs AFTER all hooks so the hook count stays stable across renders.
  if (!isAdmin) {
    return (
      <Box>
        <Alert severity="error">
          Zugriff verweigert — diese Seite ist nur für Admins zugänglich.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Azure Resources</Typography>
        {resourcesPayload && (
          <Typography variant="body2" color="text.secondary">
            {tabResources.length} von {resourcesPayload.count} {tab === 'inventory' ? 'Resources' : 'Treffer'}
            {resourcesPayload.skipToken ? ' (weitere Seiten verfügbar)' : ''}
          </Typography>
        )}
      </Box>

      {error && (
        <ErrorMessage
          message={error}
          title="Fehler beim Laden der Azure Resources"
          onRetry={refetchResources}
        />
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Name enthält"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                size="small"
                inputProps={{ 'aria-label': 'Name enthält' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Resource-Type"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                size="small"
              >
                {TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'default'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Subscription-Id(s)"
                placeholder="sub-id, sub-id"
                value={subscriptionFilter}
                onChange={(e) => setSubscriptionFilter(e.target.value)}
                size="small"
                inputProps={{ 'aria-label': 'Subscription' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Tag (key=value)"
                placeholder="Workload=cam"
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                size="small"
                inputProps={{ 'aria-label': 'Tag' }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v as TabKey)}
        sx={{ mb: 2 }}
        aria-label="Resource-Sichten"
      >
        <Tab value="inventory" label="Inventory" />
        <Tab value="drift" label="Drift" />
        <Tab value="orphans" label="Orphans" />
      </Tabs>

      <Card>
        {loading && tabResources.length === 0 ? (
          <TableSkeleton
            rows={5}
            columns={6}
            headers={['Name', 'Type', 'Subscription', 'RG', 'Power-State', 'Tags']}
          />
        ) : tabResources.length === 0 ? (
          <EmptyState
            variant="no-results"
            title={
              tab === 'drift'
                ? 'Keine Drift-Resources'
                : tab === 'orphans'
                  ? 'Keine Packer-Waisen'
                  : 'Keine Azure-Resources gefunden'
            }
            description={
              tab === 'drift'
                ? 'Alle Azure-VMs sind einem Schulungslauf zugeordnet.'
                : tab === 'orphans'
                  ? 'Keine VMs mit `Role=PackerBuild` oder `ManagedBy=Packer` aktiv.'
                  : 'Mit den aktuellen Filtern wurden keine Resources gefunden.'
            }
          />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>RG</TableCell>
                  <TableCell>Power-State</TableCell>
                  <TableCell>Tags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tabResources.map((r) => {
                  const power = shortPowerState(r.properties?.powerState);
                  return (
                    <TableRow
                      key={r.id}
                      hover
                      onClick={() => setSelected(r)}
                      sx={{ cursor: 'pointer' }}
                      aria-label={`Open metadata for ${r.name}`}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{r.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{shortType(r.type)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {r.subscriptionId.slice(0, 8)}…
                        </Typography>
                      </TableCell>
                      <TableCell>{r.resourceGroup}</TableCell>
                      <TableCell>
                        {power ? (
                          <Chip
                            label={power}
                            color={power === 'running' ? 'success' : power === 'deallocated' ? 'default' : 'warning'}
                            size="small"
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {KNOWN_TAG_KEYS.filter((k) => r.tags[k]).map((k) => (
                            <Chip key={k} label={`${k}=${r.tags[k]}`} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* NC-543: metadata side-drawer. Left half ARG fields (read-only, live
          state from Azure Resource Graph). Right half portal-stored metadata
          form — editable for admins, read-only for viewers. */}
      <Drawer
        anchor="right"
        open={!!selected}
        onClose={() => setSelected(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
      >
        {selected && (
          <Box sx={{ p: 3 }} role="region" aria-label="Resource metadata">
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6">{selected.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {shortType(selected.type)}
                </Typography>
              </Box>
              <IconButton
                aria-label="Drawer schliessen"
                onClick={() => setSelected(null)}
                size="small"
              >
                ×
              </IconButton>
            </Stack>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Azure (read-only)
            </Typography>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Typography variant="caption">
                Subscription: {selected.subscriptionId}
              </Typography>
              <Typography variant="caption">RG: {selected.resourceGroup}</Typography>
              <Typography variant="caption">Location: {selected.location}</Typography>
              {selected.properties?.powerState && (
                <Typography variant="caption">
                  Power-State: {shortPowerState(selected.properties.powerState)}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {Object.entries(selected.tags).map(([k, v]) => (
                  <Chip key={k} label={`${k}=${v}`} size="small" variant="outlined" />
                ))}
              </Box>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Portal-Metadaten {isAdmin ? '(editierbar)' : '(read-only)'}
            </Typography>

            {metadataLoading ? (
              <Typography variant="caption" color="text.secondary">
                Metadaten werden geladen …
              </Typography>
            ) : (
              <Stack spacing={2}>
                <TextField
                  label="Notes"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  multiline
                  minRows={3}
                  size="small"
                  fullWidth
                  disabled={!isAdmin || saving}
                  inputProps={{ 'aria-label': 'Metadata Notes', maxLength: 4000 }}
                />
                <TextField
                  label="Owner"
                  value={formOwner}
                  onChange={(e) => setFormOwner(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={!isAdmin || saving}
                  inputProps={{ 'aria-label': 'Metadata Owner', maxLength: 200 }}
                />
                <TextField
                  label="Business Context"
                  value={formBusinessContext}
                  onChange={(e) => setFormBusinessContext(e.target.value)}
                  multiline
                  minRows={2}
                  size="small"
                  fullWidth
                  disabled={!isAdmin || saving}
                  inputProps={{ 'aria-label': 'Metadata Business Context', maxLength: 4000 }}
                />
                <TextField
                  label="Linked RunId"
                  value={formLinkedRunId}
                  onChange={(e) => setFormLinkedRunId(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={!isAdmin || saving}
                  inputProps={{ 'aria-label': 'Metadata Linked RunId', maxLength: 200 }}
                />

                {saveError && (
                  <Alert severity="error" onClose={() => setSaveError(null)}>
                    {saveError}
                  </Alert>
                )}

                {metadata && (
                  <Typography variant="caption" color="text.secondary">
                    Zuletzt aktualisiert: {new Date(metadata.updatedAt).toLocaleString('de-DE')} ·
                    {' '}von {metadata.updatedBy}
                  </Typography>
                )}

                {isAdmin && (
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    aria-label="Metadaten speichern"
                  >
                    {saving ? 'Speichert…' : 'Speichern'}
                  </Button>
                )}
              </Stack>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
